import { MouseEvent, useState } from 'react';

import { Button, Divider, Stack } from '@mui/material';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';

import { CreditCategory, PaymentMethodStatus } from '@/graphql';
import useAddPaymentMethod from '@/hooks/useAddPaymentMethod';
import useMonetaryCreditInfo, {
  useOrgCreditBalances,
  useOrgSharedEndpointsCreditBalance,
} from '@/hooks/useMonetaryCreditInfo';
import useOrg from '@/hooks/useOrg';
import { getBorder } from '@/theme/utils';
import { formatPrice } from '@/utils/currency';

import { HybridCreditCallout } from './CreditCallout/HybridCreditCallout';
import { HybridDotIcon, Button as ProgressButton } from './styled';

function Credits() {
  const { credits: legacySharedEndpointCredits } = useOrgSharedEndpointsCreditBalance();
  const { monetaryCreditInfo } = useMonetaryCreditInfo();
  const { creditBalances } = useOrgCreditBalances();
  const addPaymentMethod = useAddPaymentMethod();
  const org = useOrg();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const isOpen = Boolean(anchorEl);

  // Needed for both pre and post Orb migration.
  if (!org || !monetaryCreditInfo) {
    return null;
  }

  const isOrbBillingEnabled = org.orbMigrationCompletedAt;

  const { paymentMethodStatus } = monetaryCreditInfo;
  const needsCreditCard = paymentMethodStatus === PaymentMethodStatus.NeedsPaymentMethod;
  const hasCreditCard = paymentMethodStatus === PaymentMethodStatus.HasPaymentMethod;

  if (isOrbBillingEnabled) {
    // Post-migration check.
    if (!creditBalances) {
      return null;
    }
  } else if (
    // Pre-migration check.
    !legacySharedEndpointCredits ||
    (legacySharedEndpointCredits.remaining <= 0 &&
      monetaryCreditInfo.creditBalance <= 0 &&
      !needsCreditCard)
  ) {
    return null;
  }

  const dedicatedInferenceBalance = creditBalances?.find(
    ({ category }) => category === CreditCategory.DedicatedInference,
  );

  const trainingBalance = creditBalances?.find(
    ({ category }) => category === CreditCategory.Training,
  );

  const modelApisBalance = creditBalances?.find(
    ({ category }) => category === CreditCategory.ModelApis,
  );

  if (isOrbBillingEnabled) {
    const dedicatedInferenceRemaining = dedicatedInferenceBalance?.remaining ?? 0;
    const trainingRemaining = trainingBalance?.remaining ?? 0;
    const modelApisRemaining = modelApisBalance?.remaining ?? 0;

    // If the org is out of credits but they have a
    // payment method, no need to show credits.
    if (
      dedicatedInferenceRemaining <= 0 &&
      trainingRemaining <= 0 &&
      modelApisRemaining <= 0 &&
      !needsCreditCard
    ) {
      return null;
    }
  }

  let dedicatedCreditsRemainingCents = 0;
  let dedicatedCreditsIssuedCents = 0;

  let trainingCreditsRemainingCents = 0;
  let trainingCreditsIssuedCents = 0;

  let modelApiCreditsRemainingCents = 0;
  let modelApiCreditsIssuedCents = 0;

  if (isOrbBillingEnabled) {
    dedicatedCreditsRemainingCents = (dedicatedInferenceBalance?.remaining ?? 0) * 100;
    dedicatedCreditsIssuedCents = (dedicatedInferenceBalance?.issued ?? 0) * 100;

    trainingCreditsRemainingCents = (trainingBalance?.remaining ?? 0) * 100;
    trainingCreditsIssuedCents = (trainingBalance?.issued ?? 0) * 100;

    modelApiCreditsRemainingCents = (modelApisBalance?.remaining ?? 0) * 100;
    modelApiCreditsIssuedCents = (modelApisBalance?.issued ?? 0) * 100;
  } else {
    dedicatedCreditsRemainingCents = monetaryCreditInfo.creditBalance;
    dedicatedCreditsIssuedCents = monetaryCreditInfo.creditGranted;

    modelApiCreditsRemainingCents = legacySharedEndpointCredits.remaining * 100;
    modelApiCreditsIssuedCents = legacySharedEndpointCredits.issued * 100;
  }

  const totalCreditsRemainingCents =
    dedicatedCreditsRemainingCents + modelApiCreditsRemainingCents + trainingCreditsRemainingCents;
  const totalCreditsIssuedCents =
    dedicatedCreditsIssuedCents + modelApiCreditsIssuedCents + trainingCreditsIssuedCents;

  const totalProgress =
    (100 * (totalCreditsIssuedCents - totalCreditsRemainingCents)) / totalCreditsIssuedCents;

  const dedicatedDescription =
    needsCreditCard && dedicatedCreditsRemainingCents <= 0
      ? 'Add a payment method to reactivate your models and deploy new versions.'
      : `${formatPrice(
          dedicatedCreditsRemainingCents,
        )} of free credits remaining to deploy and serve models.`;

  const trainingDescription =
    needsCreditCard && trainingCreditsRemainingCents <= 0
      ? 'Add a payment method to create new training jobs.'
      : `${formatPrice(trainingCreditsRemainingCents)} of free credits remaining to train models.`;

  const modelApisDescription =
    needsCreditCard && modelApiCreditsRemainingCents <= 0
      ? 'Add a payment method to reactivate your Model APIs.'
      : `${formatPrice(
          modelApiCreditsRemainingCents,
        )} of free credits remaining to use Model APIs.`;

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  return (
    <>
      <ProgressButton progress={0} onClick={handleClick} hasCreditCard={hasCreditCard}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography zIndex={1} variant="h5">
            <Stack direction="row">
              <HybridDotIcon hasCreditCard={hasCreditCard} progress={totalProgress} />
              {formatPrice(totalCreditsRemainingCents)}
            </Stack>
          </Typography>
          <Typography
            zIndex={1}
            color="secondary"
            sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}
          >
            remaining
          </Typography>
        </Stack>
      </ProgressButton>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        elevation={3}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Stack gap={2} p={2}>
          <HybridCreditCallout
            title="Dedicated deployments"
            description={dedicatedDescription}
            creditsRemainingCents={dedicatedCreditsRemainingCents}
            creditsIssuedCents={dedicatedCreditsIssuedCents}
            hasCreditCard={hasCreditCard}
          />

          {trainingCreditsIssuedCents > 0 && (
            <>
              <Divider />
              <HybridCreditCallout
                title="Training"
                description={trainingDescription}
                creditsRemainingCents={trainingCreditsRemainingCents}
                creditsIssuedCents={trainingCreditsIssuedCents}
                hasCreditCard={hasCreditCard}
              />
            </>
          )}

          <Divider />
          <HybridCreditCallout
            title="Model APIs"
            description={modelApisDescription}
            creditsRemainingCents={modelApiCreditsRemainingCents}
            creditsIssuedCents={modelApiCreditsIssuedCents}
            hasCreditCard={hasCreditCard}
          />
        </Stack>
        {needsCreditCard && (
          <Stack p={2} sx={{ borderTop: (theme) => getBorder(theme.palette.border.default) }}>
            <Button variant="contained" color="primary" onClick={addPaymentMethod}>
              Add payment method
            </Button>
          </Stack>
        )}
      </Popover>
    </>
  );
}

export default Credits;
